import React, { useState, useCallback, useEffect } from "react";
import './custom.css';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  Button,
  IconButton,
  Divider,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Grid,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import truncate from 'truncate-html';

  const EmailList = React.forwardRef(({ 
    mid, 
    folder, 
    getEmailProps, 
    handleInitialEmailView, 
    handleMobileEmailView 
  }, ref) => {
    const [messages, setMessages] = useState([]);
    const [emailFolder, setEmailFolder] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState("");

    const clearFolder = useCallback(() => {
      setEmailFolder([]);
    }, []);

    const setFolders = useCallback((data) => {
      const folderData = [];
      if (folder === "Inbox") {
        data.forEach((value) => {
          (value.recipients?.recipient || []).forEach((val) => {
            if (val.mid === mid) folderData.push(value);
          });
        });
      } else if (folder === "Sent") {
        data.forEach((value) => {
          if (value.sender?.mid === mid) folderData.push(value);
        });
      } else {
        data.forEach((value) => folderData.push(value));
      }
      setEmailFolder(folderData);
    }, [mid, folder]);

    const getEmailList = useCallback(() => {
      axios.get(process.env.REACT_APP_OMS_MESSAGE_URL, {
        params: { mid },
        headers: {
          'Authorization': "Bearer " + sessionStorage.getItem('token'),
          'Content-Type': 'multipart/form-data',
          'Cache-Control': 'No-Store'
        }
      })
        .then(res => {
          setFolders(res.data.message || []);
          setMessages(res.data.message || []);
        })
        .catch(error => {
          console.error('Error fetching emails:', error);
        });
    }, [mid, setFolders]);

    React.useImperativeHandle(ref, () => ({
      clearFolder,
      getEmailList
    }));

    useEffect(() => {
      if (mid) {
        getEmailList();
      }
    }, [mid, folder, getEmailList]);

    const readMessage = (messageId) => {
      const url = process.env.REACT_APP_OMS_MESSAGE_URL + messageId;
      axios.get(url, {
        headers: {
          "Authorization": "Bearer " + sessionStorage.getItem("token"),
          'Cache-Control': 'No-Store'
        }
      }).catch((err) => console.error("Error reading message:", err));
    };

    const setEmailProps = (messageId, name, subject, body, date, attachment) => {
      const attId = (attachment || []).map((value) => value.attachment_id);
      const files = [];
      attId.forEach((value) => {
        const url = process.env.REACT_APP_OMS_ATTACHMENTS_URL + value;
        axios.get(url, {
          responseType: "blob",
          headers: {
            "Authorization": "Bearer " + sessionStorage.getItem("token"),
            'Cache-Control': 'No-Store'
          }
        })
        .then((res) => {
          files.push(URL.createObjectURL(res.data));
          getEmailProps(name, subject, body, date, files, folder);
          handleInitialEmailView(true);
          handleMobileEmailView(true);
          readMessage(messageId);
        })
        .catch((err) => console.error("Error fetching attachment:", err));
      });
    };

    const setEmailProps1 = (messageId, name, subject, body, date) => {
      readMessage(messageId);
      getEmailProps(name, subject, body, date, [], folder);
      // Do not call handleInitialEmailView or handleMobileEmailView here
    };

    const handleClose = () => setDeleteDialogOpen(false);

    const handleOpen = (event, value) => {
      event.stopPropagation();
      setDeleteDialogOpen(true);
      setDeleteId(value);
    };

    const handleDelete = () => {
      const url = process.env.REACT_APP_OMS_MESSAGE_URL + deleteId;
      axios.delete(url, {
        headers: {
          "Authorization": "Bearer " + sessionStorage.getItem('token'),
          'Cache-Control': 'No-Store'
        }
      }).then(() => {
        setDeleteDialogOpen(false);
        setDeleteId("");
        setTimeout(() => {
          clearFolder();
          getEmailList();
          handleMobileEmailView(false);
        }, 100);
      }).catch(() => alert("There was an error while deleting the message."));
    };

    const truncateBody = (str = "") => {
      if (str.includes("<title>")) {
        const index = str.indexOf("</title>");
        const substr = str.substring(index + 9);
        return truncate(substr, 10, { byWords: true, stripTags: true });
      }
      return truncate(str, 10, { byWords: true, stripTags: true });
    };

    const truncateSub = (str = "") => (str.length > 35 ? str.substring(0, 32) + "..." : str);

    const truncateDate = (date = "") => {
      let sdate = '' + date;
      let time = sdate.substring(11, 16);
      sdate = sdate.substring(5, 10).replace('-', '/');
      return sdate + ' ' + time;
    };

    return (
      <Box sx={{ width: '100%', maxWidth: '100%', bgcolor: 'background.paper' }}>
        <List sx={{ width: '100%' }}>
          {emailFolder.map((value, i) => (
            <Box key={i}>
              <ListItem
                button
                alignItems="flex-start"
                className="email-list-item"
                sx={{
                  px: 2,
                  py: 1.25,
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => {
                  if (value.attachments) {
                    setEmailProps(
                      value.message_id,
                      value.sender?.name || '',
                      value.subject || '',
                      value.body || '',
                      value.date_created || '',
                      value.attachments.attachment
                    );
                  } else {
                    setEmailProps1(
                      value.message_id,
                      value.sender?.name || '',
                      value.subject || '',
                      value.body || '',
                      value.date_created || ''
                    );
                  }
                  // Do not call handleInitialEmailView or handleMobileEmailView here; MailboxList manages view state
                }}
                secondaryAction={
                  <IconButton
                    aria-label="delete"
                    onClick={(event) => handleOpen(event, value.message_id)}
                    edge="end"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ color: 'white', backgroundColor: '#4648c2' }} alt={value.sender?.name || ''}>
                    {value.sender?.name ? value.sender.name.charAt(0) : ''}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography noWrap component="div" sx={{ fontWeight: 600 }}>
                        {value.sender?.name || ''}
                      </Typography>
                      <Typography
                        component="div"
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}
                      >
                        {truncateDate(value.date_created)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="div">
                      <Typography
                        component="div"
                        variant="body2"
                        color="text.primary"
                        noWrap
                        className="email-subject"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {truncateSub(value.subject || '')}
                      </Typography>
                      <Typography
                        component="div"
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        className="email-body-preview"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: '100%'
                        }}
                      >
                        {truncateBody(value.body || '')}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <Divider component="li" sx={{ opacity: 0.6 }} />
            </Box>
          ))}
        </List>
        <Dialog open={deleteDialogOpen} onClose={handleClose} aria-labelledby="deleteDialog">
          <DialogTitle id="deleteDialog">{"Are you sure you want to delete this message?"}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This action will permanently delete this mail, if you still wish to continue click Delete or click Cancel to go back.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">Cancel</Button>
            <Button onClick={handleDelete} color="primary" autoFocus>Delete</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  });

  EmailList.displayName = 'EmailList';
  export default EmailList;